import React, { useState } from "react";
import { Button } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

const AiOverviewTable = ({ 
  result, 
  title = "Generated AI Overview", 
  onCopy = null,
  className = "mb-4"
}) => {
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);

  if (!result || !Array.isArray(result) || result.length === 0) {
    return null;
  }

  const handleRowCopy = async (content, index) => {
    try {
      let textToCopy;
      if (typeof content === "object" && content !== null && !Array.isArray(content)) {
        // Handle object with question as key and answer as value
        const entries = Object.entries(content);
        textToCopy = entries.map(([question, answer]) => `Question: ${question}\n\nAnswer: ${answer}`).join('\n\n---\n\n');
      } else if (typeof content === "string") {
        textToCopy = content;
      } else {
        textToCopy = JSON.stringify(content, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedRowIndex(index);
      if (onCopy) onCopy("AI Overview item copied to clipboard!");

      // Reset the copied row indicator after 2 seconds
      setTimeout(() => {
        setCopiedRowIndex(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy row content: ", err);
    }
  };

  const handleCopyAll = async () => {
    try {
      const textToCopy = result.map((item, index) => {
        if (typeof item === "object" && item !== null && !Array.isArray(item)) {
          const entries = Object.entries(item);
          const qaText = entries.map(([question, answer]) => `Question: ${question}\nAnswer: ${answer}`).join('\n\n');
          return `${index + 1}. ${qaText}`;
        } else if (typeof item === "string") {
          return `${index + 1}. ${item}`;
        } else {
          return `${index + 1}. ${JSON.stringify(item)}`;
        }
      }).join("\n\n");
      
      await navigator.clipboard.writeText(textToCopy);
      if (onCopy) onCopy("AI Overview copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy all content: ", err);
    }
  };

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{title}</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCopyAll}
        >
          <i className="fas fa-copy me-1"></i>
          Copy All
        </Button>
      </div>
      
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th scope="col" style={{ width: "60px", color: "black" }}>
                #
              </th>
              <th scope="col" style={{ color: "black" }}>
                AI Overview
              </th>
              <th scope="col" style={{ width: "100px", color: "black" }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {result.map((item, index) => (
              <tr key={index}>
                <th scope="row">{index + 1}</th>
                <td>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {typeof item === "object" && item !== null && !Array.isArray(item) ? (
                      <div>
                        {Object.entries(item).map(([question, answer], entryIndex) => (
                          <div key={entryIndex}>
                            <div className="text-primary fw-bold mb-2" style={{ fontSize: "14px" }}>
                              {question}
                            </div>
                            <div className="text-success" style={{ fontSize: "14px" }}>
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <p className="mb-1">{children}</p>
                                  ),
                                  strong: ({ children }) => (
                                    <strong className="fw-bold">{children}</strong>
                                  ),
                                  em: ({ children }) => <em>{children}</em>,
                                  ul: ({ children }) => (
                                    <ul
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children }) => (
                                    <li className="mb-0">{children}</li>
                                  ),
                                  h1: ({ children }) => (
                                    <h6 className="fw-bold mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h2: ({ children }) => (
                                    <h6 className="fw-bold mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h3: ({ children }) => (
                                    <h6 className="fw-bold mb-1">{children}</h6>
                                  ),
                                  code: ({ children }) => (
                                    <code className="bg-light px-1 rounded small">
                                      {children}
                                    </code>
                                  ),
                                }}
                              >
                                {answer}
                              </ReactMarkdown>
                            </div>
                            {entryIndex < Object.entries(item).length - 1 && (
                              <hr className="my-2" style={{ opacity: 0.3 }} />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : typeof item === "string" ? (
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className="mb-1">{children}</p>
                          ),
                          strong: ({ children }) => (
                            <strong className="fw-bold">{children}</strong>
                          ),
                          em: ({ children }) => <em>{children}</em>,
                          ul: ({ children }) => (
                            <ul
                              className="mb-1"
                              style={{ paddingLeft: "15px" }}
                            >
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol
                              className="mb-1"
                              style={{ paddingLeft: "15px" }}
                            >
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="mb-0">{children}</li>
                          ),
                          h1: ({ children }) => (
                            <h6 className="fw-bold text-primary mb-1">
                              {children}
                            </h6>
                          ),
                          h2: ({ children }) => (
                            <h6 className="fw-bold text-primary mb-1">
                              {children}
                            </h6>
                          ),
                          h3: ({ children }) => (
                            <h6 className="fw-bold mb-1">{children}</h6>
                          ),
                          code: ({ children }) => (
                            <code className="bg-light px-1 rounded small">
                              {children}
                            </code>
                          ),
                        }}
                      >
                        {item}
                      </ReactMarkdown>
                    ) : (
                      <pre className="mb-0 small text-wrap">
                        {JSON.stringify(item, null, 2)}
                      </pre>
                    )}
                  </div>
                </td>
                <td>
                  <Button
                    variant={
                      copiedRowIndex === index
                        ? "success"
                        : "outline-primary"
                    }
                    size="sm"
                    onClick={() => handleRowCopy(item, index)}
                    className="px-2"
                  >
                    <i
                      className={`fas ${
                        copiedRowIndex === index ? "fa-check" : "fa-copy"
                      } me-1`}
                    ></i>
                    {copiedRowIndex === index ? "Copied!" : "Copy"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AiOverviewTable;
